import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { BUILD_SHA, isStale } from '../../lib/build.js';
import { useI18n } from '../../i18n/index.js';

// "I deployed it but I can't see that anything changed."
//
// The system had no answer to that from the inside. A deployed change reaches
// a browser only when the browser asks for the page again — index.html is
// no-cache, so a reload is enough — but a tab that has been open since
// yesterday keeps running the JavaScript it loaded yesterday, and nothing on
// screen says so. On a station PC that tab can be open for a week.
//
// So the bundle carries the commit it was built from, the server reports the
// commit it is running, and this compares them. When they differ it says so.
//
// It never reloads by itself. A kiosk that reloads under a worker's hands
// mid-scan loses whatever they were doing, and the whole point of this notice
// is to stop surprising people — replacing a silent staleness with a sudden
// page reload would be trading one surprise for a worse one.
const POLL_MS = 60_000;

export function StaleBuildNotice(): JSX.Element | null {
  const [serverSha, setServerSha] = useState<string | null>(null);
  const [serverTime, setServerTime] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  // הבאנר יושב מעל עשרה מסכים מתורגמים, כולל קיוסקים של הרצפה — שם דווקא
  // יושב מי שאינו קורא עברית. ראו edal-i18n-child-components.
  const { t, dir } = useI18n();

  useEffect(() => {
    let alive = true;

    const check = async (): Promise<void> => {
      try {
        const res = await fetch('/api/health', { credentials: 'include' });
        if (!res.ok && res.status !== 503) return;
        // 503 still carries a body: the server is up but the schema is behind,
        // which is exactly a moment when knowing the build matters.
        const body = (await res.json()) as { build?: { sha?: string; time?: string } };
        if (alive && body.build?.sha) {
          setServerSha(body.build.sha);
          setServerTime(body.build.time ?? null);
        }
      } catch {
        // Offline, or the server is restarting mid-deploy. Not worth a message:
        // the next tick answers, and a network blip must not produce a scary
        // banner on the shop floor.
      }
    };

    void check();
    const id = setInterval(() => void check(), POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (dismissed || !isStale(serverSha, serverTime)) return null;

  return (
    <div
      role="status"
      className="flex items-center justify-center gap-3 flex-wrap bg-amber-50 border-b border-amber-300 px-4 py-2 text-sm text-amber-900 print:hidden"
      dir={dir}
    >
      <RefreshCw className="w-4 h-4 flex-shrink-0" />
      <span className="font-semibold">{t('build.newVersion')}</span>
      <span className="text-amber-800">
        {t('build.stillOld')}
        {/* U+200E so the two shas do not swap places inside the RTL line. */}
        <span dir="ltr"> ({BUILD_SHA} → {serverSha}) </span>
      </span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="bg-navy-600 hover:bg-navy-700 text-white font-bold px-3 py-1 rounded-lg"
      >
        {t('build.reloadNow')}
      </button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-amber-700 hover:text-amber-900 underline"
      >
        {t('build.notNow')}
      </button>
    </div>
  );
}
